import React, { FC, useState, useEffect, useRef } from 'react'
import { PmsJobRecord, PmsJobCompletionDto, PmsJobAttachmentFileType, PmsPlanLineDto, RunningHourCounterDto } from '../../core/pms/_models'
import { completePmsJob, completePlanLineJob, listWorkOrdersByPlanLine, uploadJobAttachments, getRunningHourCounters } from '../../core/pms/_requests'
import { getPartsBySubcomponent, getSubcomponentsByEquipment } from '../../core/_requests'
import { PartDto, SubcomponentDto } from '../../core/_models'
import { WorkOrderDetailsModal } from './WorkOrderDetailsModal'
import { PlanLineLastDoneModal } from './PlanLineLastDoneModal'
import { RunningHoursModal } from './RunningHoursModal'
import { formatJobDate } from './pmsJobHelpers'
import { useAuth } from '../../../auth'
import { toast } from 'react-toastify'
import { RiskAssessmentNumberSection } from './RiskAssessmentNumberSection'
import { riskNumberIsCriticalEquipment, shouldExcludeCriticalEquipment } from './pmsRiskAssessmentPicker'
import SearchableSelect from '../../../../shared/components/SearchableSelect'

type JobWithPlanLine = PmsJobRecord & {
  __planLine?: PmsPlanLineDto
}

interface Props {
  visible: boolean
  onClose: () => void
  job: JobWithPlanLine
  onSuccess: () => void
  locked?: boolean
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 2100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const COMPLETION_ATTACHMENT_TYPE: PmsJobAttachmentFileType = 'REPORT'
const MAX_ATTACHMENT_SIZE_MB = 5
const MAX_ATTACHMENT_SIZE_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024

const FlagIcon = ({ className }: { className?: string }) => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='currentColor'
    aria-hidden='true'
    className={className}
  >
    <path d='M5 2h2v20H5z' />
    <path d='M7 3h12l-3 4 3 4H7z' />
  </svg>
)

export const CompleteJobModal: FC<Props> = ({ visible, onClose, job, onSuccess, locked = false }) => {
  const { currentUser } = useAuth()
  const isLocked = Boolean(locked)
  const todayStr = new Date().toISOString().split('T')[0]
  const lastJobIdRef = useRef<string | number | null>(null)

  const buildChecklistItems = (checklist?: string | null) => {
    if (!checklist) return []
    return checklist
      .split('\n')
      .filter((line) => line.trim().length > 0)
      .map((line, index) => ({
        id: `checklist-${index}`,
        text: line.trim(),
        checked: false,
      }))
  }

  const getDefaultFormData = (): PmsJobCompletionDto => ({
    actualCompletionDate: todayStr,
    actualCounterAtCompletion: undefined,
    readingDate: todayStr,
    remarks: '',
    findings: '',
    measuredValues: '',
    riskAssessmentNumber: '',
    flagged: false,
    flaggedComment: '',
    performerUserId: currentUser?.id || undefined,
    verifierUserId: undefined,
  })

  const [formData, setFormData] = useState<PmsJobCompletionDto>(getDefaultFormData())
  const [submitting, setSubmitting] = useState(false)
  const [checklistItems, setChecklistItems] = useState<Array<{ id: string; text: string; checked: boolean }>>([])
  const [findingsError, setFindingsError] = useState<string>('')
  const [updateRunningHours, setUpdateRunningHours] = useState(false)

  // Parts used state
  const [currentParts, setCurrentParts] = useState<PartDto[]>([])
  const [loadingParts, setLoadingParts] = useState(false)
  const [selectedSubcomponentId, setSelectedSubcomponentId] = useState<number | null>(job.subComponentId || null)
  const [subcomponents, setSubcomponents] = useState<SubcomponentDto[]>([])
  const [loadingSubcomponents, setLoadingSubcomponents] = useState(false)
  const [usedParts, setUsedParts] = useState<Set<number>>(new Set())
  const [usedPartQuantities, setUsedPartQuantities] = useState<Record<number, number>>({})
  const [activeTab, setActiveTab] = useState<'prep' | 'complete'>('prep')
  const [riskError, setRiskError] = useState<string>('')
  const [workOrders, setWorkOrders] = useState<PmsJobRecord[]>([])
  const [loadingWorkOrders, setLoadingWorkOrders] = useState(false)
  const [workOrderView, setWorkOrderView] = useState<PmsJobRecord | null>(null)
  const [workOrderViewOpen, setWorkOrderViewOpen] = useState(false)
  const [workOrdersPage, setWorkOrdersPage] = useState(1)
  const [workOrdersPageSize, setWorkOrdersPageSize] = useState(5)
  const [lastDoneModalOpen, setLastDoneModalOpen] = useState(false)
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([])
  const [counterInfo, setCounterInfo] = useState<RunningHourCounterDto | null>(null)
  const [counterLoading, setCounterLoading] = useState(false)
  const [runningHoursUpdated, setRunningHoursUpdated] = useState(false)
  const [runningHoursModalOpen, setRunningHoursModalOpen] = useState(false)
  const [raApplicable, setRaApplicable] = useState(false)

  const trimmedRiskNumber = formData.riskAssessmentNumber?.trim() || ''
  const raOkForTabs = !raApplicable || (trimmedRiskNumber.length > 0 && trimmedRiskNumber.length <= 64)
  const canProceedToComplete = raOkForTabs
  const lastRecordedValue = counterInfo?.currentValue ?? job.__planLine?.runningHourCounterValue
  const lastRecordedName = counterInfo?.name ?? job.__planLine?.runningHourCounterName
  const lastRecordedDate = counterInfo?.lastReadingDate
  const lastRecordedDateText = lastRecordedDate ? new Date(lastRecordedDate).toLocaleDateString() : ''
  const completionDate = formData.actualCompletionDate || todayStr
  const hasReadingForToday = Boolean(
    counterInfo?.lastReadingDate && counterInfo.lastReadingDate >= completionDate
  )

  const resetDraft = () => {
    setFormData(getDefaultFormData())
    setChecklistItems(buildChecklistItems(job.checklist))
    setSelectedSubcomponentId(job.subComponentId || null)
    setUsedParts(new Set())
    setUsedPartQuantities({})
    setActiveTab('prep')
    setRiskError('')
    setRunningHoursUpdated(false)
    setAttachmentFiles([])
    setWorkOrdersPage(1)
    setFindingsError('')
    setUpdateRunningHours(false)
    setRunningHoursModalOpen(false)
    setRaApplicable(false)
  }

  useEffect(() => {
    if (!visible) return
    const planLineId = job.planLineId || (job as any).__planLine?.id
    const jobId = job.id ? `job-${job.id}` : (planLineId ? `plan-${planLineId}` : null)
    if (lastJobIdRef.current !== jobId) {
      lastJobIdRef.current = jobId
      resetDraft()
    }
  }, [visible, job.id, job.planLineId, job.checklist, job.subComponentId, currentUser?.id])

  useEffect(() => {
    if (visible) {
      loadWorkOrders()
    }
  }, [visible, job.planLineId, job.id, job.state, job.defectId, job.jobDueReason, job.dueDate])

  // Load subcomponents for equipment-level jobs
  useEffect(() => {
    if (visible && job.equipmentId && !job.subComponentId) {
      setLoadingSubcomponents(true)
      getSubcomponentsByEquipment(job.equipmentId)
        .then((data) => {
          setSubcomponents(data)
        })
        .catch((error) => {
          console.error('Error loading subcomponents:', error)
          toast.error('Failed to load subcomponents')
        })
        .finally(() => setLoadingSubcomponents(false))
    } else {
      setSubcomponents([])
    }
  }, [visible, job.equipmentId, job.subComponentId])

  // Load parts when subcomponent is selected
  useEffect(() => {
    if (visible && selectedSubcomponentId) {
      setLoadingParts(true)
      getPartsBySubcomponent(selectedSubcomponentId)
        .then((data) => {
          setCurrentParts(data)
        })
        .catch((error) => {
          console.error('Error loading parts:', error)
          toast.error('Failed to load parts')
          setCurrentParts([])
        })
        .finally(() => setLoadingParts(false))
    } else {
      setCurrentParts([])
    }
  }, [visible, selectedSubcomponentId])

  const loadWorkOrders = async () => {
    if (!visible || !job.planLineId) {
      setWorkOrders([])
      return
    }
    setLoadingWorkOrders(true)
    try {
      const data = await listWorkOrdersByPlanLine(job.planLineId)
      const filtered = data.filter((wo) => wo.id !== job.id)
      const getSortTime = (wo: PmsJobRecord) => {
        const raw = wo.actualCompletionDate || wo.updatedAt || wo.createdAt || ''
        const dt = raw ? new Date(raw).getTime() : 0
        return Number.isNaN(dt) ? 0 : dt
      }
      const sorted = [...filtered].sort((a, b) => getSortTime(b) - getSortTime(a))
      setWorkOrders(sorted)
      setWorkOrdersPage(1)
      const excludeCe = shouldExcludeCriticalEquipment(job)
      const pickDefaultRiskFromHistory = (): string | null => {
        for (const wo of sorted) {
          const r = (wo.riskAssessmentNumber || '').trim()
          if (!r) continue
          if (excludeCe && riskNumberIsCriticalEquipment(r)) continue
          return r
        }
        return null
      }
      const defaultRa = pickDefaultRiskFromHistory()
      if (defaultRa) {
        setFormData((prev) =>
          prev.riskAssessmentNumber && prev.riskAssessmentNumber.trim().length > 0
            ? prev
            : { ...prev, riskAssessmentNumber: defaultRa }
        )
        setRaApplicable(true)
        setRiskError('')
      }
    } catch (error) {
      console.error('Error loading work orders:', error)
      toast.error('Failed to load work orders', { position: 'top-center' })
      setWorkOrders([])
    } finally {
      setLoadingWorkOrders(false)
    }
  }

  const handleLastDoneSaved = () => {
    loadWorkOrders()
  }

  useEffect(() => {
    if (!visible) return
    setUsedParts(new Set())
    setUsedPartQuantities({})
  }, [selectedSubcomponentId, visible])

  useEffect(() => {
    if (!updateRunningHours) {
      setRunningHoursUpdated(false)
    }
  }, [updateRunningHours])

  // Determine if running hours field should be shown and required
  const shouldShowRunningHours = () => {
    const scheduleType = job.scheduleType
    if (!scheduleType) return false

    // Always show for RUNNING_HOURS and HYBRID types
    if (
      scheduleType === 'RUNNING_HOURS' ||
      scheduleType === 'HYBRID_WHICHEVER_FIRST' ||
      scheduleType === 'HYBRID_BOTH_REQUIRED'
    ) {
      return true
    }

    // For EVENT type, show if plan line exists and has running hours periodicity
    if (scheduleType === 'EVENT' && job.planLineId) {
      // Check if periodicityId is 'H' (hours) or has periodicityId2 as 'H'
      return job.periodicityId === 'H' || job.periodicityId2 === 'H'
    }

    return false
  }

  const fetchCounterInfo = async (counterId: number): Promise<RunningHourCounterDto | null> => {
    try {
      const counters = await getRunningHourCounters(job.vesselId)
      const list = Array.isArray(counters) ? counters : []
      return list.find((c) => c.id === counterId) ?? null
    } catch (error) {
      console.error('Error loading running hour counters:', error)
      return null
    }
  }

  useEffect(() => {
    if (!visible || !shouldShowRunningHours()) {
      setCounterInfo(null)
      return
    }
    const counterId = job.__planLine?.runningHourCounterId
    if (!counterId) {
      setCounterInfo(null)
      return
    }
    setCounterLoading(true)
    fetchCounterInfo(counterId)
      .then((data) => setCounterInfo(data))
      .finally(() => setCounterLoading(false))
  }, [
    visible,
    job.__planLine?.runningHourCounterId,
    job.scheduleType,
    job.periodicityId,
    job.periodicityId2,
  ])

  const refreshCounterInfo = async () => {
    const counterId = job.__planLine?.runningHourCounterId
    if (!counterId) {
      setCounterInfo(null)
      return
    }
    setCounterLoading(true)
    try {
      const data = await fetchCounterInfo(counterId)
      setCounterInfo(data)
    } finally {
      setCounterLoading(false)
    }
  }

  const toggleUsedPart = (partId: number) => {
    setUsedParts((prev) => {
      const next = new Set(prev)
      if (next.has(partId)) {
        next.delete(partId)
        setUsedPartQuantities((qtyPrev) => {
          const nextQty = { ...qtyPrev }
          delete nextQty[partId]
          return nextQty
        })
      } else {
        next.add(partId)
        setUsedPartQuantities((qtyPrev) => ({
          ...qtyPrev,
          [partId]: qtyPrev[partId] && qtyPrev[partId] > 0 ? qtyPrev[partId] : 1,
        }))
      }
      return next
    })
  }

  const clampUsedQuantity = (value: number, robQty: number) => {
    if (!Number.isFinite(value) || value < 1) return 1
    if (value > robQty) return robQty
    return Math.floor(value)
  }

  const updateUsedPartQuantity = (partId: number, rawValue: string, robQty: number) => {
    const parsed = Number(rawValue)
    const nextValue = clampUsedQuantity(parsed, robQty)
    setUsedPartQuantities((prev) => ({
      ...prev,
      [partId]: nextValue,
    }))
  }

  const filterAttachmentFiles = (files: File[]) => {
    const oversized = files.filter((file) => file.size > MAX_ATTACHMENT_SIZE_BYTES)
    if (oversized.length > 0) {
      toast.error(`Each attachment must be ${MAX_ATTACHMENT_SIZE_MB} MB or smaller.`, { position: 'top-center' })
    }
    return files.filter((file) => file.size <= MAX_ATTACHMENT_SIZE_BYTES)
  }

  if (!visible) return null

  const totalWorkOrders = workOrders.length
  const workOrdersTotalPages = Math.max(1, Math.ceil(totalWorkOrders / workOrdersPageSize))
  const workOrdersPageSafe = Math.min(workOrdersPage, workOrdersTotalPages)
  const workOrdersStart = (workOrdersPageSafe - 1) * workOrdersPageSize
  const workOrdersPageItems = workOrders.slice(workOrdersStart, workOrdersStart + workOrdersPageSize)

  const handleSubmit = async () => {
    if (isLocked) {
      toast.error('For this job to update, please contact your admin', { position: 'top-center' })
      return
    }
    // Validation
    if (raApplicable) {
      if (!trimmedRiskNumber) {
        setRiskError('Select a risk assessment number or turn off “applicable”.')
        setActiveTab('prep')
        toast.error('Risk assessment number is required when marked applicable', { position: 'top-center' })
        return
      }
      if (trimmedRiskNumber.length > 64) {
        setRiskError('Risk assessment number must be 64 characters or less')
        setActiveTab('prep')
        toast.error('Risk assessment number must be 64 characters or less', { position: 'top-center' })
        return
      }
    }

    if (!formData.actualCompletionDate) {
      toast.error('Completion date is required', { position: 'top-center' })
      return
    }

    if (!formData.findings || formData.findings.trim().length === 0) {
      setFindingsError('Findings are required')
      toast.error('Findings are required', { position: 'top-center' })
      return
    }

    // For RUNNING_HOURS and HYBRID jobs, updating running hours is only mandatory if no reading exists for completion date/today
    const requiresCounterUpdate =
      shouldShowRunningHours() &&
      (job.scheduleType === 'RUNNING_HOURS' ||
        job.scheduleType === 'HYBRID_WHICHEVER_FIRST' ||
        job.scheduleType === 'HYBRID_BOTH_REQUIRED')

    if (requiresCounterUpdate && !hasReadingForToday && !runningHoursUpdated) {
      toast.error(
        'Running hours must be updated before closing this job. Please enable "Update running hours" and add a reading.',
        { position: 'top-center' }
      )
      return
    }

    if (shouldShowRunningHours() && updateRunningHours && !runningHoursUpdated) {
      toast.error('Please add a running hours reading or turn off the override toggle.', { position: 'top-center' })
      return
    }

    const usedPartIds = Array.from(usedParts)
    // If job is equipment-level, subcomponent must be selected for used parts
    if (job.equipmentId && !job.subComponentId && !selectedSubcomponentId) {
      if (usedPartIds.length > 0) {
        toast.error('Please select a subcomponent for parts used', { position: 'top-center' })
        return
      }
    }

    const partById = new Map<number, PartDto>()
    currentParts.forEach((part) => {
      if (part.id) partById.set(part.id, part)
    })

    let invalidSpareUsageMessage: string | null = null
    const spareUsages = usedPartIds.map((partId) => {
      const sourcePart = partById.get(partId)
      const robQty = typeof sourcePart?.robQuantity === 'number' ? sourcePart.robQuantity : null
      if (robQty == null || robQty <= 0) {
        invalidSpareUsageMessage = `Part ${sourcePart?.name || partId} cannot be used because ROB is unavailable.`
        return null
      }
      const selectedQty = usedPartQuantities[partId] ?? 1
      return {
        sparePartId: partId,
        quantity: clampUsedQuantity(selectedQty, robQty),
        subcomponentId: selectedSubcomponentId || job.subComponentId || undefined,
      }
    }).filter((item): item is { sparePartId: number; quantity: number; subcomponentId: number | undefined } => !!item)

    if (invalidSpareUsageMessage) {
      toast.error(invalidSpareUsageMessage, { position: 'top-center' })
      return
    }

    setSubmitting(true)
    try {
      const resolvedCounterAtCompletion = (updateRunningHours && runningHoursUpdated)
        ? (counterInfo?.currentValue ?? undefined)
        : (hasReadingForToday ? (counterInfo?.currentValue ?? undefined) : undefined)

      const resolvedReadingDate = resolvedCounterAtCompletion != null
        ? (formData.readingDate || formData.actualCompletionDate || todayStr)
        : undefined

      const isPeriodicityPseudoJob = !job.id || job.id <= 0
      const completed = isPeriodicityPseudoJob && job.planLineId
        ? await completePlanLineJob(job.planLineId, {
            ...formData,
            riskAssessmentNumber: raApplicable ? trimmedRiskNumber : null,
            flagged: formData.flagged || false,
            flaggedComment: formData.flagged ? formData.flaggedComment : '',
            actualCounterAtCompletion: resolvedCounterAtCompletion,
            readingDate: resolvedReadingDate,
            spareUsages: spareUsages.length > 0 ? spareUsages : undefined,
            partReplacements: undefined,
            partsToRemove: undefined,
          })
        : await completePmsJob(job.id!, {
            ...formData,
            riskAssessmentNumber: raApplicable ? trimmedRiskNumber : null,
            flagged: formData.flagged || false,
            flaggedComment: formData.flagged ? formData.flaggedComment : '',
            actualCounterAtCompletion: resolvedCounterAtCompletion,
            readingDate: resolvedReadingDate,
            spareUsages: spareUsages.length > 0 ? spareUsages : undefined,
            partReplacements: undefined,
            partsToRemove: undefined,
          })
      if (attachmentFiles.length > 0) {
        try {
          await uploadJobAttachments(
            completed.id || job.id!,
            attachmentFiles,
            COMPLETION_ATTACHMENT_TYPE,
            currentUser?.username
          )
        } catch (error) {
          toast.error('Job completed but attachments failed to upload', { position: 'top-center' })
        }
      }
      toast.success('Job completed successfully')
      resetDraft()
      onSuccess()
    } catch (error) {
      toast.error('Failed to complete job')
    } finally {
      setSubmitting(false)
    }
  }
  return (
    <div style={WRAP_STYLE} tabIndex={-1} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{
          width: '75vw',
          height: '75vh',
          maxWidth: '1400px',
          maxHeight: '900px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0'>
          <div>
            <h5 className='modal-title text-dark m-0'>Complete Job</h5>
            {isLocked && (
              <div className='text-danger fs-7 mt-1'>
                For this job to update, please contact your admin.
              </div>
            )}
          </div>
          <button type='button' className='btn-close' onClick={onClose}></button>
        </div>
        <div className='flex-grow-1 d-flex flex-column' style={{ minHeight: 0 }}>
          <div className='px-4 pt-3 border-bottom flex-shrink-0'>
            <ul className='nav nav-tabs'>
              <li className='nav-item'>
                <button
                  type='button'
                  className={`nav-link ${activeTab === 'prep' ? 'active' : ''}`}
                  onClick={() => setActiveTab('prep')}
                  disabled={isLocked}
                >
                  Prep & History
                </button>
              </li>
              <li className='nav-item'>
                <button
                  type='button'
                  className={`nav-link ${activeTab === 'complete' ? 'active' : ''}`}
                  onClick={() => {
                    if (isLocked) return
                    if (canProceedToComplete) {
                      setRiskError('')
                      setActiveTab('complete')
                    } else {
                      setRiskError(
                        raApplicable ? 'Risk assessment number is required when applicable' : 'Complete prep requirements'
                      )
                      toast.error('Complete the prep tab to continue', { position: 'top-center' })
                    }
                  }}
                  disabled={isLocked || !canProceedToComplete}
                >
                  Complete Job
                </button>
              </li>
            </ul>
          </div>
          <div className='overflow-auto px-4 py-3' style={{ maxHeight: 'calc(75vh - 180px)' }}>
            {isLocked && (
              <div className='alert alert-warning py-2 px-3 mb-3'>
                For this job to update, please contact your admin.
              </div>
            )}
            <fieldset disabled={isLocked || submitting} className='border-0 p-0 m-0'>
            {activeTab === 'prep' ? (
              <>
                <div className='card bg-light mb-4'>
                  <div className='card-body p-3'>
                    <div className='d-flex flex-wrap gap-2 mb-2'>
                      {job.jobCode && <span className='badge badge-light-primary'>Job Code: {job.jobCode}</span>}
                      {job.jobType && <span className='badge badge-light-info'>{job.jobType}</span>}
                      {job.equipmentName && (
                        <span className='badge badge-light-success'>Equipment: {job.equipmentName}</span>
                      )}
                      {job.subComponentName && (
                        <span className='badge badge-light-warning'>SubComponent: {job.subComponentName}</span>
                      )}
                      {job.vesselName && <span className='badge badge-light'>Vessel: {job.vesselName}</span>}
                      {job.flagged && (
                        <span className='badge bg-danger text-white d-inline-flex align-items-center gap-1'>
                          <FlagIcon className='text-white' />
                          Flagged
                        </span>
                      )}
                    </div>
                    <div className='fw-semibold'>{job.jobDescription || job.title || '-'}</div>
                    {job.jobInstructions && (
                      <div className='mt-2'>
                        <div className='text-muted fs-8 fw-semibold'>Job Instructions</div>
                        <div className='text-dark fs-7' style={{ whiteSpace: 'pre-wrap' }}>
                          {job.jobInstructions}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <RiskAssessmentNumberSection
                  job={job}
                  applicable={raApplicable}
                  onApplicableChange={setRaApplicable}
                  value={formData.riskAssessmentNumber || ''}
                  onValueChange={(v) => {
                    setFormData({ ...formData, riskAssessmentNumber: v })
                    setRiskError('')
                  }}
                  errorText={riskError}
                  onClearError={() => setRiskError('')}
                />

                <div className='mb-4'>
                  <h6 className='fw-bold mb-3'>Previous Work Orders</h6>
                  <div className='card bg-light'>
                    <div className='card-body p-0'>
                      {loadingWorkOrders ? (
                        <div className='text-center py-4'>
                          <span className='spinner-border spinner-border-sm'></span> Loading history...
                        </div>
                      ) : workOrders.length === 0 ? (
                        <div className='text-muted text-center py-4'>
                          <small>No previous work orders found.</small>
                          {job.planLineId && (
                            <div className='mt-3'>
                              <button
                                type='button'
                                className='btn btn-sm btn-primary'
                                onClick={() => setLastDoneModalOpen(true)}
                              >
                                Add Last Done
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className='table-responsive' style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          <table className='table table-sm table-bordered align-middle mb-0'>
                            <thead className='table-light sticky-top'>
                              <tr>
                                <th style={{ width: '18%' }}>Completed Date</th>
                                <th style={{ width: '15%' }}>Job Code</th>
                                <th style={{ width: '24%' }}>Findings</th>
                                <th style={{ width: '15%' }}>Flagged</th>
                                <th style={{ width: '18%' }}>Flagged Comment</th>
                                <th style={{ width: '10%' }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {workOrdersPageItems.map((wo) => (
                                <tr key={wo.id} className={wo.flagged ? 'table-warning' : ''}>
                                  <td>{formatJobDate(wo.actualCompletionDate || wo.updatedAt || wo.createdAt)}</td>
                                  <td>{wo.jobCode || '-'}</td>
                                  <td>
                                    <div
                                      className='text-truncate'
                                      style={{ maxWidth: '260px' }}
                                      title={wo.findings || ''}
                                    >
                                      {wo.findings || '-'}
                                    </div>
                                  </td>
                                  <td>
                                    {wo.flagged ? (
                                      <span
                                        className='badge bg-danger text-white d-inline-flex align-items-center gap-1'
                                        title={wo.flaggedComment || 'Flagged'}
                                      >
                                        <FlagIcon className='text-white' />
                                        Flagged
                                      </span>
                                    ) : (
                                      <span className='text-muted'>-</span>
                                    )}
                                  </td>
                                  <td>
                                    <div
                                      className='text-truncate'
                                      style={{ maxWidth: '180px' }}
                                      title={wo.flaggedComment || ''}
                                    >
                                      {wo.flaggedComment || '-'}
                                    </div>
                                  </td>
                                  <td>
                                    <button
                                      type='button'
                                      className='btn btn-sm btn-light-primary'
                                      onClick={() => {
                                        setWorkOrderView(wo)
                                        setWorkOrderViewOpen(true)
                                      }}
                                    >
                                      View Work Order
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {workOrders.length > 0 && (
                        <div className='d-flex align-items-center justify-content-between px-3 py-2 border-top'>
                          <div className='text-muted fs-8'>
                            Showing {workOrdersStart + 1}-{Math.min(workOrdersStart + workOrdersPageSize, totalWorkOrders)} of {totalWorkOrders}
                          </div>
                          <div className='d-flex align-items-center gap-2'>
                            <SearchableSelect
                              className='form-select form-select-sm text-dark'
                              style={{ width: '90px' }}
                              value={workOrdersPageSize}
                              onChange={(e) => {
                                setWorkOrdersPageSize(Number(e.target.value))
                                setWorkOrdersPage(1)
                              }}
                            >
                              <option value={5}>5</option>
                              <option value={10}>10</option>
                              <option value={20}>20</option>
                            </SearchableSelect>
                            <button
                              type='button'
                              className='btn btn-sm btn-light'
                              disabled={workOrdersPageSafe <= 1}
                              onClick={() => setWorkOrdersPage((prev) => Math.max(1, prev - 1))}
                            >
                              Prev
                            </button>
                            <div className='text-muted fs-8'>
                              Page {workOrdersPageSafe} of {workOrdersTotalPages}
                            </div>
                            <button
                              type='button'
                              className='btn btn-sm btn-light'
                              disabled={workOrdersPageSafe >= workOrdersTotalPages}
                              onClick={() =>
                                setWorkOrdersPage((prev) => Math.min(workOrdersTotalPages, prev + 1))
                              }
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className='card bg-light mb-4'>
                  <div className='card-body p-3'>
                    <div className='d-flex flex-wrap gap-2 mb-2'>
                      {job.jobCode && <span className='badge badge-light-primary'>Job Code: {job.jobCode}</span>}
                      {job.jobType && <span className='badge badge-light-info'>{job.jobType}</span>}
                      {job.equipmentName && (
                        <span className='badge badge-light-success'>Equipment: {job.equipmentName}</span>
                      )}
                      {job.subComponentName && (
                        <span className='badge badge-light-warning'>SubComponent: {job.subComponentName}</span>
                      )}
                      {job.vesselName && <span className='badge badge-light'>Vessel: {job.vesselName}</span>}
                      {(job.flagged || formData.flagged) && (
                        <span className='badge bg-danger text-white d-inline-flex align-items-center gap-1'>
                          <FlagIcon className='text-white' />
                          Flagged
                        </span>
                      )}
                    </div>
                    <div className='fw-semibold'>{job.jobDescription || job.title || '-'}</div>
                    {job.jobInstructions && (
                      <div className='mt-2'>
                        <div className='text-muted fs-8 fw-semibold'>Job Instructions</div>
                        <div className='text-dark fs-7' style={{ whiteSpace: 'pre-wrap' }}>
                          {job.jobInstructions}
                        </div>
                      </div>
                    )}
                    {raApplicable && trimmedRiskNumber && (
                      <div className='text-muted fs-7 mt-2'>
                        Risk Assessment Number: <strong>{trimmedRiskNumber}</strong>
                      </div>
                    )}
                    {raApplicable && !trimmedRiskNumber && (
                      <div className='text-warning fs-7 mt-2'>Risk assessment marked applicable — select a number on Prep tab.</div>
                    )}
                  </div>
                </div>

                <div className='row g-3'>
                  <div className='col-md-6'>
                    <label className={LABEL}>
                      Completion Date <span className='text-danger'>*</span>
                    </label>
                    <input
                      type='date'
                      className='form-control text-dark'
                      required
                      value={formData.actualCompletionDate || ''}
                      onChange={(e) => setFormData({ ...formData, actualCompletionDate: e.target.value })}
                    />
                  </div>
                  {shouldShowRunningHours() && (
                    <div className='col-md-12'>
                      <div className='d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2'>
                        <div className='form-check form-switch'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            id='update-running-hours'
                            checked={updateRunningHours}
                            onChange={(e) => setUpdateRunningHours(e.target.checked)}
                          />
                          <label className='form-check-label fw-semibold' htmlFor='update-running-hours'>
                            Update running hours for this completion
                            {(job.scheduleType === 'RUNNING_HOURS' ||
                              job.scheduleType === 'HYBRID_WHICHEVER_FIRST' ||
                              job.scheduleType === 'HYBRID_BOTH_REQUIRED') && !hasReadingForToday && (
                              <span className='text-danger ms-1' title='Required when no reading is recorded for today'>*</span>
                            )}
                          </label>
                        </div>
                        {counterInfo && (
                          <button
                            type='button'
                            className='btn btn-light-primary btn-sm'
                            onClick={() => setRunningHoursModalOpen(true)}
                          >
                            {hasReadingForToday ? 'Override Reading' : 'Open Add Reading Modal'}
                          </button>
                        )}
                      </div>
                      {hasReadingForToday && !updateRunningHours && (
                        <div className='alert alert-info py-2 px-3 mb-2 d-flex align-items-center justify-content-between'>
                          <div>
                            <span className='fw-bold'>Reading already recorded for {completionDate === todayStr ? 'today' : completionDate}:</span>{' '}
                            <span className='badge badge-light-primary fs-7'>
                              {counterInfo?.currentValue?.toLocaleString()} hrs
                            </span>{' '}
                            <span className='text-muted fs-8'>({counterInfo?.name || 'Counter'})</span>
                            <div className='text-muted fs-8 mt-1'>
                              This reading will be recorded for this completion. Enable the toggle above only if you need to override it.
                            </div>
                          </div>
                        </div>
                      )}
                      {updateRunningHours && (
                        <div className='border rounded p-2 bg-light-subtle'>
                          {runningHoursUpdated ? (
                            <div className='text-success fw-semibold'>Running hours reading updated.</div>
                          ) : (
                            <div className='text-muted'>
                              {hasReadingForToday
                                ? 'Use the Override Reading modal to enter a new reading value.'
                                : 'Use the Add Reading modal to record the new running hours.'}
                            </div>
                          )}
                          {lastRecordedValue != null && (
                            <div className='text-muted fs-8 mt-1'>
                              Last recorded value{lastRecordedName ? ` (${lastRecordedName})` : ''}:{' '}
                              {lastRecordedValue.toLocaleString()} hrs
                              {lastRecordedDateText ? ` • ${lastRecordedDateText}` : ''}
                            </div>
                          )}
                          {counterLoading && (
                            <div className='text-muted fs-8 mt-1'>Loading counter details...</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <div className='col-md-12'>
                    <label className={LABEL}>Remarks</label>
                    <textarea
                      className='form-control text-dark'
                      rows={3}
                      value={formData.remarks || ''}
                      onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                    />
                  </div>
                  <div className='col-md-12'>
                    <label className={LABEL}>
                      Findings <span className='text-danger'>*</span>
                    </label>
                    <textarea
                      className={`form-control text-dark ${findingsError ? 'is-invalid' : ''}`}
                      rows={3}
                      value={formData.findings || ''}
                      onChange={(e) => {
                        setFormData({ ...formData, findings: e.target.value })
                        if (findingsError) setFindingsError('')
                      }}
                    />
                    {findingsError && <div className='invalid-feedback d-block'>{findingsError}</div>}
                  </div>
                  <div className='col-md-12'>
                    <label className={LABEL}>Measured Values</label>
                    <textarea
                      className='form-control text-dark'
                      rows={2}
                      value={formData.measuredValues || ''}
                      onChange={(e) => setFormData({ ...formData, measuredValues: e.target.value })}
                      placeholder='Enter measured values (e.g., pressure, temperature, etc.)'
                    />
                  </div>
                  <div className='col-md-12'>
                    <div className='form-check form-switch mb-2'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id='flag-job'
                        checked={!!formData.flagged}
                        onChange={(e) => {
                          const checked = e.target.checked
                          setFormData((prev) => ({
                            ...prev,
                            flagged: checked,
                            flaggedComment: checked ? prev.flaggedComment : '',
                          }))
                        }}
                      />
                      <label className='form-check-label fw-semibold' htmlFor='flag-job'>
                        Flag this job
                      </label>
                    </div>
                    <textarea
                      className='form-control text-dark'
                      rows={2}
                      placeholder='Add flagged comment (optional)'
                      value={formData.flaggedComment || ''}
                      disabled={!formData.flagged}
                      onChange={(e) => setFormData({ ...formData, flaggedComment: e.target.value })}
                    />
                  </div>

                  <div className='col-md-12'>
                    <label className={LABEL}>Attachments</label>
                    <input
                      type='file'
                      multiple
                      className='form-control text-dark'
                      onChange={(e) => {
                        const files = e.target.files ? Array.from(e.target.files) : []
                        const validFiles = filterAttachmentFiles(files)
                        if (validFiles.length > 0) {
                          setAttachmentFiles((prev) => [...prev, ...validFiles])
                          e.target.value = ''
                        }
                      }}
                    />
                    <div className='text-muted fs-8 mt-1'>Max {MAX_ATTACHMENT_SIZE_MB} MB per file</div>
                    {attachmentFiles.length > 0 && (
                      <div className='mt-2'>
                        {attachmentFiles.map((file, idx) => (
                          <div
                            key={`${file.name}-${idx}`}
                            className='d-flex align-items-center justify-content-between border rounded px-2 py-1 mb-1'
                          >
                            <div className='text-truncate' style={{ maxWidth: '75%' }}>
                              {file.name}{' '}
                              <span className='text-muted fs-8'>({Math.round(file.size / 1024)} KB)</span>
                            </div>
                            <button
                              type='button'
                              className='btn btn-sm btn-light-danger'
                              onClick={() => setAttachmentFiles((prev) => prev.filter((_, i) => i !== idx))}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Checklist Section */}
                  {checklistItems.length > 0 && (
                    <div className='mb-5'>
                      <label className={LABEL}>Checklist</label>
                      <div className='card bg-light p-3'>
                        <div className='form-check-list'>
                          {checklistItems.map((item) => (
                            <div key={item.id} className='form-check mb-2'>
                              <input
                                className='form-check-input'
                                type='checkbox'
                                id={item.id}
                                checked={item.checked}
                                onChange={(e) => {
                                  setChecklistItems((prev) =>
                                    prev.map((i) =>
                                      i.id === item.id ? { ...i, checked: e.target.checked } : i
                                    )
                                  )
                                }}
                              />
                              <label className='form-check-label' htmlFor={item.id}>
                                {item.text}
                              </label>
                            </div>
                          ))}
                        </div>
                        <div className='mt-3 pt-3 border-top'>
                          <small className='text-muted'>
                            Completed: {checklistItems.filter((i) => i.checked).length} / {checklistItems.length}
                          </small>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Parts Used Section */}
                <div className='mb-5'>
                  <label className={LABEL}>Parts Used</label>
                  <div className='text-muted mb-2' style={{ fontSize: '0.85rem' }}>
                    Check the parts used during this job. Set the used quantity for each selected spare. This may
                    trigger a requisition if it falls below the minimum threshold.
                  </div>

                  {/* Subcomponent Selection for Equipment-level Jobs */}
                  {job.equipmentId && !job.subComponentId && (
                    <div className='mb-3'>
                      <label className='form-label fw-semibold fs-7 mb-2 text-dark'>
                        Select Subcomponent <span className='text-danger'>*</span>
                      </label>
                      <SearchableSelect
                        className='form-select text-dark'
                        value={selectedSubcomponentId || ''}
                        onChange={(e) => setSelectedSubcomponentId(e.target.value ? Number(e.target.value) : null)}
                        disabled={loadingSubcomponents}
                        required
                      >
                        <option value=''>Select Subcomponent</option>
                        {subcomponents.map((sub) => (
                          <option key={sub.id} value={sub.id}>
                            {sub.name}
                          </option>
                        ))}
                      </SearchableSelect>
                    </div>
                  )}

                  <div className='card bg-light p-3'>
                    <div className='mb-3 text-muted' style={{ fontSize: '0.9rem' }}>
                      Select the spare parts that were consumed.
                    </div>
                    <div className='d-flex flex-wrap gap-2 mb-3'>
                      <span className='badge bg-danger-subtle text-danger'>ROB 0 (disabled)</span>
                      <span className='badge bg-warning-subtle text-warning'>Below min threshold</span>
                      <span className='badge bg-secondary-subtle text-muted'>Missing UOM/ROB/Min (disabled)</span>
                    </div>

                    {loadingParts ? (
                      <div className='text-center py-3'>
                        <span className='spinner-border spinner-border-sm'></span> Loading parts...
                      </div>
                    ) : currentParts.length === 0 ? (
                      <div className='text-muted text-center py-3'>
                        <small>No parts available for this subcomponent</small>
                      </div>
                    ) : (
                      <div className='table-responsive' style={{ maxHeight: '350px', overflowY: 'auto' }}>
                        <table className='table table-sm table-bordered'>
                          <thead className='table-light sticky-top'>
                            <tr>
                              <th style={{ width: '35%' }}>Part</th>
                              <th style={{ width: '15%' }}>ROB Qty</th>
                              <th style={{ width: '15%' }}>UOM</th>
                              <th style={{ width: '15%' }}>Min Threshold</th>
                              <th style={{ width: '15%' }}>Used</th>
                              <th style={{ width: '20%' }}>Used Qty</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentParts.map((part) => {
                              const robQty = typeof part.robQuantity === 'number' ? part.robQuantity : null
                              const minThreshold = typeof part.minThreshold === 'number' ? part.minThreshold : null
                              const uomLabel = part.unitOfMeasure || part.uom?.code || ''
                              const missingUsageData = !uomLabel && robQty === null && minThreshold === null
                              const robMissing = robQty === null
                              const robLow = robQty !== null && robQty <= 0
                              const belowThreshold =
                                robQty !== null && minThreshold !== null && robQty < minThreshold
                              const isDisabled = !part.id || missingUsageData || robMissing || robLow
                              const isChecked = part.id ? usedParts.has(part.id) : false
                              const selectedQty =
                                part.id && robQty !== null && robQty > 0
                                  ? clampUsedQuantity(usedPartQuantities[part.id] ?? 1, robQty)
                                  : 1
                              return (
                                <tr
                                  key={part.id}
                                  className={isDisabled ? 'table-secondary' : ''}
                                  style={{ opacity: isDisabled ? 0.7 : 1 }}
                                >
                                  <td>
                                    <div className='fw-semibold'>{part.name}</div>
                                    <small className='text-muted'>{part.code || part.itemCode || '-'}</small>
                                    {missingUsageData && (
                                      <div className='text-danger fs-8 mt-1'>
                                        Missing UOM / ROB / Min threshold. Add these to enable usage.
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    <span className={robLow ? 'text-danger fw-semibold' : ''}>
                                      {robQty ?? '-'}
                                    </span>
                                    {robLow && (
                                      <div className='text-danger fs-8'>No spares available</div>
                                    )}
                                    {robMissing && (
                                      <div className='text-danger fs-8'>ROB not available</div>
                                    )}
                                    {!robLow && belowThreshold && (
                                      <div className='text-warning fs-8'>Below min threshold</div>
                                    )}
                                  </td>
                                  <td>{uomLabel || '-'}</td>
                                  <td>{minThreshold ?? '-'}</td>
                                  <td className='text-center'>
                                    <div className='form-check form-switch d-inline-flex justify-content-center'>
                                      <input
                                        type='checkbox'
                                        className='form-check-input'
                                        style={{ width: '42px', height: '22px', cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                                        checked={isChecked}
                                        onChange={() => part.id && toggleUsedPart(part.id)}
                                        disabled={isDisabled}
                                        title={
                                          isDisabled
                                            ? missingUsageData
                                              ? 'Missing UOM / ROB / Min threshold'
                                              : robMissing
                                              ? 'ROB quantity is not available'
                                              : 'No spares available'
                                            : 'Mark as used'
                                        }
                                      />
                                    </div>
                                  </td>
                                  <td>
                                    <input
                                      type='number'
                                      className='form-control form-control-sm'
                                      min={1}
                                      max={robQty != null && robQty > 0 ? robQty : 1}
                                      step={1}
                                      value={selectedQty}
                                      disabled={isDisabled || !isChecked}
                                      onChange={(e) =>
                                        part.id &&
                                        robQty != null &&
                                        robQty > 0 &&
                                        updateUsedPartQuantity(part.id, e.target.value, robQty)
                                      }
                                    />
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {currentParts.length > 0 && (
                      <div className='text-muted text-center py-2'>
                        <small>
                          Selected: {usedParts.size} | Total Used Qty:{' '}
                          {Array.from(usedParts).reduce((sum, partId) => sum + (usedPartQuantities[partId] ?? 1), 0)}
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
            </fieldset>
          </div>
        </div>
        <div className='border-top d-flex justify-content-between align-items-center px-4 py-3 flex-shrink-0'>
          <div>
            {activeTab === 'complete' && (
              <button type='button' className='btn btn-light btn-sm' onClick={() => setActiveTab('prep')}>
                Back
              </button>
            )}
          </div>
          <div className='d-flex gap-2'>
            <button type='button' className='btn btn-light-danger btn-sm' onClick={resetDraft} disabled={isLocked || submitting}>
              Clear Draft
            </button>
            <button type='button' className='btn btn-light btn-sm' onClick={onClose}>
              Cancel
            </button>
            {activeTab === 'prep' ? (
              <button
                type='button'
                className='btn btn-primary btn-sm'
                onClick={() => {
                  if (isLocked) return
                  if (raApplicable) {
                    if (!trimmedRiskNumber) {
                      setRiskError('Risk assessment number is required when marked applicable')
                      return
                    }
                    if (trimmedRiskNumber.length > 64) {
                      setRiskError('Risk assessment number must be 64 characters or less')
                      return
                    }
                  }
                  setRiskError('')
                  setActiveTab('complete')
                }}
                disabled={isLocked}
              >
                Next
              </button>
            ) : (
              <button type='button' className='btn btn-primary btn-sm' onClick={handleSubmit} disabled={submitting || isLocked}>
                {submitting ? 'Submitting...' : 'Complete Job'}
              </button>
            )}
          </div>
        </div>
      </div>
      {workOrderViewOpen && workOrderView && (
        <WorkOrderDetailsModal
          visible={workOrderViewOpen}
          workOrder={workOrderView}
          onClose={() => {
            setWorkOrderViewOpen(false)
            setWorkOrderView(null)
          }}
        />
      )}
      {job.planLineId && (
        <PlanLineLastDoneModal
          visible={lastDoneModalOpen}
          onClose={() => setLastDoneModalOpen(false)}
          job={job}
          currentCounterValue={counterInfo?.currentValue ?? job.__planLine?.runningHourCounterValue ?? null}
          onSaved={handleLastDoneSaved}
        />
      )}
      {runningHoursModalOpen && counterInfo && (
        <RunningHoursModal
          visible={runningHoursModalOpen}
          counter={counterInfo}
          onClose={() => setRunningHoursModalOpen(false)}
          onSuccess={async () => {
            setRunningHoursModalOpen(false)
            setRunningHoursUpdated(true)
            await refreshCounterInfo()
          }}
        />
      )}
    </div>
  )
}
